import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { LocalBusinessSchema } from "./LocalBusinessSchema";
import StickyCallBar from "@/components/StickyCallBar";

// Defer the exit-intent popup — it never runs during initial paint, so we
// keep it out of the main bundle and only hydrate it after the page is idle.
const ExitIntentPopup = lazy(() =>
  import("./ExitIntentPopup").then((m) => ({ default: m.ExitIntentPopup })),
);

interface SiteLayoutProps {
  children: React.ReactNode;
}

export const SiteLayout = ({ children }: SiteLayoutProps) => {
  const { pathname } = useLocation();
  const [mountDeferred, setMountDeferred] = useState(false);

  // Scroll to top on route change for SPA navigation.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname]);

  // Mount non-critical UI (exit-intent popup) after the browser is idle so
  // it never blocks LCP or TTI on first paint.
  useEffect(() => {
    if (mountDeferred) return;
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const schedule = w.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 2000));
    const id = schedule(() => setMountDeferred(true));
    return () => {
      if (typeof id === "number") window.clearTimeout(id);
    };
  }, [mountDeferred]);


  return (
    <>
      <LocalBusinessSchema />
      {/* Skip to content for keyboard / screen-reader users */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-gold focus:text-navy focus:font-bold focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to content
      </a>
      <SiteHeader />
      <main id="main">{children}</main>
      <SiteFooter />
      <StickyCallBar />
      {mountDeferred && (
        <Suspense fallback={null}>
          <ExitIntentPopup />
        </Suspense>
      )}
    </>
  );
};
